import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowRight, Plus, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ClientDto, clientsApi, handleApiError } from '../services/clientsService';
import TabContainer from '../components/TabContainer';
import ClientInfoTab from '../components/clientcase/ClientInfoTab';
import ClientScheduleTab from '../components/clientcase/ClientScheduleTab';
import { CommitteesProcessTab } from '../components/committees';
import BillingTab from '../components/clientcase/BillingTab';
import SpecialPaymentsTab from '../components/clientcase/SpecialPaymentsTab';
import OverviewTab from '../components/clientcase/OverviewTab';
import AiAnalysisTab from '../components/clientcase/AiAnalysisTab';
import NewDocumentsTab from '../components/documents/NewDocumentsTab';
import EditClientModal from '../components/clientcase/EditClientModal';
import ActivityTimeline from '../components/ActivityTimeline';
import ActivityForm from '../components/ActivityForm';
import Modal from '../components/Modal';
import {
  EntityType,
  CreateActivityDto,
  createActivityWithReminder
} from '../services/activityService';

const PageContainer = styled.div`
  padding: 24px;
  direction: rtl;
  background: #ffffff;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  padding: 16px 24px;
  margin-bottom: 16px;
  /* No box styling - unified canvas */

  @media (max-width: 767px) {
    padding: 12px 16px;
  }
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    gap: 12px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 20px;
  padding: 8px;
  border-radius: var(--border-radius-md);
  transition: all 0.2s;
  width: 36px;
  height: 36px;

  &:hover {
    color: var(--primary-color);
    background: var(--hover-background);
  }
`;

const ClientInfo = styled.div`
  flex: 1;
`;

const ClientName = styled.h1`
  font-size: 22px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 767px) {
    font-size: 20px;
  }
`;

const ClientIdNumber = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      case 'danger':
        return `
          background: #dc2626;
          color: white;
          &:hover { background: #b91c1c; }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover { background: var(--hover-background); }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryHeaderButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  padding: 10px 20px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--primary-color);
    color: white;
  }

  @media (max-width: 767px) {
    justify-content: center;
  }
`;

const ErrorMessage = styled.div`
  background: var(--error-light);
  color: #dc2626;
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 20px;
  border: 1px solid #dc2626;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 16px;
  color: var(--text-secondary);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px;
  color: var(--text-secondary);
  font-style: italic;
`;

const CommunicationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const CommunicationTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
`;

const Toast = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%) translateY(${props => props.$visible ? '0' : '-100px'});
  background: #16a34a;
  color: white;
  padding: 14px 24px;
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-heavy);
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 500;
  z-index: 9999;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: all 0.3s ease;
`;

interface ClientManagementPageProps {}

const ClientManagementPage: React.FC<ClientManagementPageProps> = () => {
  const { clientId, tab, folderId } = useParams<{ clientId: string; tab?: string; folderId?: string }>();
  const navigate = useNavigate();
  const { isStaff, canViewClientFinance, user } = useAuth();

  // State
  const [client, setClient] = useState<ClientDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Main tab state - initialize from URL param or default to 'overview'
  // If folderId is present, we're in documents tab
  const [mainActiveTab, setMainActiveTab] = useState<string>(() => {
    if (folderId) return 'documents';
    return tab || 'overview';
  });

  // Modal state
  const [showEditClientModal, setShowEditClientModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);

  // Toast state
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Activity state
  const [activityCount, setActivityCount] = useState<number>(0);
  const [activityRefreshKey, setActivityRefreshKey] = useState<number>(0);

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => {
        setToastMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const loadClient = useCallback(async () => {
    if (!clientId) {
      setError('מזהה לקוח חסר');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const clientData = await clientsApi.getById(parseInt(clientId));
      setClient(clientData);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  // Handle tab changes - update URL when tab changes
  const handleTabChange = useCallback((newTab: string) => {
    setMainActiveTab(newTab);
    // Update the URL without causing a page reload
    const newPath = newTab === 'overview'
      ? `/clients/${clientId}/manage`
      : `/clients/${clientId}/manage/${newTab}`;
    navigate(newPath, { replace: true });
  }, [clientId, navigate]);

  // Sync tab with URL changes (e.g., browser back/forward)
  useEffect(() => {
    const validTabs = [
      'overview',
      'client-medical',
      'ai-analysis',
      'documents',
      'schedule-tasks',
      'process-committees',
      ...(canViewClientFinance ? ['billing', 'special-payments'] : []),
      'communication'
    ];

    // If folderId is present, we're in documents tab
    if (folderId) {
      setMainActiveTab('documents');
      return;
    }

    const currentTab = tab || 'overview';

    // Validate tab from URL
    if (validTabs.includes(currentTab)) {
      setMainActiveTab(currentTab);
    } else {
      // If invalid tab, redirect to default
      setMainActiveTab('overview');
      navigate(`/clients/${clientId}/manage`, { replace: true });
    }
  }, [tab, folderId, clientId, navigate, canViewClientFinance]);

  useEffect(() => {
    loadClient();
  }, [loadClient]);

  const formatDateOnly = (dateString?: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatDateTime = (dateString?: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleActivitySubmit = async (activity: CreateActivityDto, reminderDateTime: string | null) => {
    try {
      await createActivityWithReminder(
        activity,
        reminderDateTime
          ? { dueDateTime: reminderDateTime, entityName: client?.fullName ?? '', userId: user?.id ?? 0 }
          : null
      );
      setShowActivityModal(false);
      setToastMessage('פעילות נוצרה בהצלחה');
      // Refresh the activity timeline
      setActivityRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error creating activity:', error);
      setToastMessage('שגיאה ביצירת פעילות');
      throw error;
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>טוען פרטי לקוח...</LoadingContainer>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorMessage>{error}</ErrorMessage>
        <Button onClick={() => navigate('/clients')}>חזרה לרשימת לקוחות</Button>
      </PageContainer>
    );
  }

  if (!client) {
    return (
      <PageContainer>
        <EmptyState>לקוח לא נמצא</EmptyState>
        <Button onClick={() => navigate('/clients')}>חזרה לרשימת לקוחות</Button>
      </PageContainer>
    );
  }

  const canManageDocuments = isStaff;

  // Build tabs array
  const tabs = [
    {
      key: 'overview',
      title: 'סקירה כללית',
      content: (
        <OverviewTab
          client={client}
          formatDateOnly={formatDateOnly}
          formatDateTime={formatDateTime}
          onNavigateToTab={handleTabChange}
        />
      )
    },
    {
      key: 'client-medical',
      title: 'מידע אישי ורפואי',
      content: (
        <ClientInfoTab
          client={client}
          formatDateOnly={formatDateOnly}
          formatDateTime={formatDateTime}
          onEdit={() => setShowEditClientModal(true)}
        />
      )
    },
    {
      key: 'ai-analysis',
      title: 'ניתוח AI',
      content: (
        <AiAnalysisTab clientId={client.id} />
      )
    },
    {
      key: 'documents',
      title: 'מסמכים',
      content: (
        <>
          {canManageDocuments ? (
            <NewDocumentsTab
              clientId={client.id}
              clientName={client.fullName}
              folderId={folderId ? parseInt(folderId) : undefined}
            />
          ) : (
            <EmptyState>אין הרשאה לצפייה במסמכים</EmptyState>
          )}
        </>
      )
    },
    {
      key: 'schedule-tasks',
      title: 'תיאום ומשימות',
      content: (
        <ClientScheduleTab
          clientId={client.id}
          clientName={client.fullName}
          clientEmail={client.email}
        />
      )
    },
    {
      key: 'process-committees',
      title: 'תהליך וועדות',
      content: (
        <CommitteesProcessTab
          clientId={client.id}
          clientName={client.fullName}
        />
      )
    },
    ...(canViewClientFinance ? [
      {
        key: 'billing',
        title: 'חיובים ותשלומים',
        content: (
          <BillingTab
            clientId={client.id}
            clientName={client.fullName}
          />
        )
      },
      {
        key: 'special-payments',
        title: 'תשלומים מיוחדים',
        content: (
          <SpecialPaymentsTab
            clientId={client.id}
            clientName={client.fullName}
          />
        )
      }
    ] : []),
    {
      key: 'communication',
      title: 'היסטוריית תקשורת',
      content: (
        <div style={{ padding: '24px' }}>
          <CommunicationHeader>
            <CommunicationTitle>היסטוריית תקשורת ({activityCount})</CommunicationTitle>
            <PrimaryHeaderButton onClick={() => setShowActivityModal(true)}>
              <Plus size={16} strokeWidth={2} />
              הוסף פעילות חדשה
            </PrimaryHeaderButton>
          </CommunicationHeader>

          <ActivityTimeline
            entityType={EntityType.Client}
            entityId={client.id}
            showAddButton={false}
            hideHeader={true}
            onCountChange={setActivityCount}
            refreshKey={activityRefreshKey}
          />
        </div>
      )
    }
  ];

  return (
    <PageContainer>
      {/* Toast Notification */}
      <Toast $visible={!!toastMessage}>
        <CheckCircle size={18} />
        {toastMessage}
      </Toast>

      {/* Page Header with Client Info */}
      <PageHeader>
        <HeaderTop>
          <BackButton onClick={() => navigate('/clients')} title="חזרה ללקוחות">
            <ArrowRight size={20} />
          </BackButton>
          <ClientInfo>
            <ClientName>{client.fullName}</ClientName>
            <ClientIdNumber>ת.ז: {client.idNumber}</ClientIdNumber>
          </ClientInfo>
        </HeaderTop>
      </PageHeader>

      {/* Main Tab Container */}
      <TabContainer
        tabs={tabs}
        activeTab={mainActiveTab}
        onChange={handleTabChange}
      />

      {/* Modals */}
      {showEditClientModal && client && (
        <EditClientModal
          isOpen={showEditClientModal}
          onClose={() => setShowEditClientModal(false)}
          onSuccess={(updatedClient) => {
            setClient(updatedClient);
            setShowEditClientModal(false);
          }}
          client={client}
        />
      )}

      {showActivityModal && client && (
        <Modal
          isOpen={showActivityModal}
          onClose={() => setShowActivityModal(false)}
          title="הוסף פעילות חדשה"
          width="700px"
        >
          <ActivityForm
            entityType={EntityType.Client}
            entityId={client.id}
            onSubmit={handleActivitySubmit}
            onCancel={() => setShowActivityModal(false)}
          />
        </Modal>
      )}


    </PageContainer>
  );
};

export default ClientManagementPage;
